import React, { useRef, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Paper, Button, Typography } from "@material-ui/core";
import { useFormik } from "formik";
import useStyles from "@/styles/alarmStyle";
import SearchInput from "@/components/SearchInput";
import { useTranslation } from "@/langs/useTranslation";
import { toast } from "@/apis/request";
import { FormItem } from "@/components/Form";
import {
  applyNamingApi,
  deleteNamingApi,
  getNamingGroupList,
  updateNamingStatus
} from "@/apis/apiCollection";
import modalConfirm from "@/components/Modal/modalConfirm";
import SubmitButton from "@/components/commons/SubmitButton";
import { useGlobalState } from "@/GlobalProvider";
import HasPermission from "@/components/commons/HasPermission";
import OSType from "@/components/FormFields/OSType";
import DeviceNamingStatus from "@/components/FormFields/DeviceNamingStatus";
import { DeviceNamingDatatable } from "@/helpers/DeviceNamingDatatable";
import ViewMerchantsModal from "./ViewMerchantsModal";
import TransferGroupModal from "@/containers/GroupsUsers/Category/TransferGroupModal";
import { CompanyDeviceNamingDatatable } from "@/helpers/CompanyDeviceNamingDatatable";

const NamingList = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const tableRef = useRef(null);
  const tableRef2 = useRef(null);
  const selectedRowsRef = useRef([]);
  const isReset = useRef(true);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [viewMerchantModalOpen, setViewMerchantModalOpen] = useState(false);
  const [transferGroupModalOpen, setTransferGroupModalOpen] = useState(false);
  const updateType = useRef(null);
  const currentRow = useRef(null);
  const currentRowGroupIds = useRef(null);

  const history = useHistory();

  const initialValues = {
    osType: undefined,
    status: -1,
    keywords: ""
  };

  const { currentCompanyId } = useGlobalState();

  const { values, handleChange, handleSubmit /* , handleReset */, resetForm } =
    useFormik({
      initialValues,
      onSubmit: () => {
        handleReloadDataTable();
      }
    });

  const getSearchParams = () => {
    const formValues = isReset.current ? initialValues : values;
    return {
      osType: formValues.osType,
      status: formValues.status ?? -1,
      keywords: formValues.keywords,
      level: 1
    };
  };

  const reloadTableWithGroupId = () => {
    isReset.current = false;
    tableRef.current?.reload();
  };

  const resetTableWithGroupId = () => {
    isReset.current = true;
    tableRef.current?.reload();
  };

  const reloadTable2WithGroupId = () => {
    tableRef2.current?.reload();
  };

  const resetTable2WithGroupId = () => {
    tableRef2.current?.reload();
  };

  const handleReloadDataTable2 = reset => {
    if (reset) {
      resetTable2WithGroupId();
    } else {
      reloadTable2WithGroupId();
    }
  };

  const handleReloadDataTable = reset => {
    if (reset) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId();
    }
  };

  const resetQuery = () => {
    resetForm({
      values: initialValues
    });
    handleReloadDataTable(true);
  };

  const getSelectedRows = () => {
    const selectedRows = tableRef.current.getSelectedRows();
    if (selectedRows && selectedRows.length) {
      selectedRowsRef.current = selectedRows;
      return selectedRows;
    }
    toast.info(t({ defaultMessage: "未选择任何数据" }));
  };

  const handleCreate = () => {
    history.push("/setting/device-naming/create");
  };

  const handleAssociated = async row => {
    currentRow.current = row;
    const { groups } = await getNamingGroupList({ namingRuleId: row.id });
    currentRowGroupIds.current = groups.map(group => group.id);
    setTransferGroupModalOpen(true);
  };

  const handleTransferGroupConfirm = async (checkedKeys, uncheckedKeys) => {
    await applyNamingApi({
      namingRuleId: currentRow.current.id,
      groupIds: checkedKeys
    });
    setTransferGroupModalOpen(false);
    handleReloadDataTable();
  };

  const handleLoaded = json => {
    setTotal(json.recordsTotal);
  };

  const handleViewMerchants = row => {
    currentRow.current = row;
    setViewMerchantModalOpen(true);
  };

  const doEnable = async (rows, enable) => {
    try {
      updateType.current = enable ? "enable" : "disable";
      setLoading(true);
      if (!Array.isArray(rows)) {
        rows = [rows];
      }
      await updateNamingStatus({
        namingRuleIds: rows.map(row => row.id),
        status: enable ? 0 : 1
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "操作成功" }));
    } finally {
      setLoading(false);
    }
  };

  const doDeviceNameEnable = async (rows, enable) => {
    if (!Array.isArray(rows)) {
      rows = [rows];
    }
    await updateNamingStatus({
      namingRuleIds: rows.map(row => row.id),
      status: enable ? 0 : 1
    });
    handleReloadDataTable2();
    toast.success(t({ defaultMessage: "操作成功" }));
  };

  const doDelete = async rows => {
    try {
      updateType.current = "delete";
      setLoading(true);
      if (!Array.isArray(rows)) {
        rows = [rows];
      }
      await deleteNamingApi({
        namingRuleIds: rows.map(item => item.id)
      });
      handleReloadDataTable();
      toast.success(t({ defaultMessage: "删除成功" }));
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = (row, enable) => {
    modalConfirm({
      title: enable
        ? t({ defaultMessage: "启用命名规则" })
        : t({ defaultMessage: "禁用命名规则" }),
      content: enable
        ? t(
            {
              defaultMessage: "是否确认启用（{name}）该命名规则？"
            },
            { name: row.name }
          )
        : t(
            {
              defaultMessage: "是否确认禁用（{name}）该命名规则？"
            },
            { name: row.name }
          ),
      onConfirm: () => {
        return doEnable(row, enable);
      }
    });
  };

  const handleDelete = row => {
    modalConfirm({
      title: t({ defaultMessage: "删除命名规则" }),
      content: t(
        {
          defaultMessage: "是否确认删除（{name}）该命名规则？"
        },
        { name: row.name }
      ),
      onConfirm: () => {
        return doDelete(row);
      }
    });
  };

  const handleEdit = row => {
    history.push("/setting/device-naming/edit/" + row.id);
  };

  const handleView = row => {
    history.push("/setting/device-naming/info/" + row.id);
  };

  const handleBatchEnable = enable => {
    if (getSelectedRows()) {
      const name = [
        ...new Set(selectedRowsRef.current.map(item => item.name))
      ].join("、");
      modalConfirm({
        title: enable
          ? t({ defaultMessage: "启用命名规则" })
          : t({ defaultMessage: "禁用命名规则" }),
        content: enable
          ? t(
              {
                defaultMessage: "是否确认启用（{name}）该命名规则？"
              },
              { name }
            )
          : t(
              {
                defaultMessage: "是否确认禁用（{name}）该命名规则？"
              },
              { name }
            ),
        onConfirm: () => {
          return doEnable(selectedRowsRef.current, enable);
        }
      });
    }
  };

  const handleBatchDelete = () => {
    if (getSelectedRows()) {
      modalConfirm({
        title: t({ defaultMessage: "删除命名规则" }),
        content: t(
          {
            defaultMessage: "是否确认删除（{name}）该命名规则？"
          },
          {
            name: [
              ...new Set(selectedRowsRef.current.map(item => item.name))
            ].join("、")
          }
        ),
        onConfirm: () => {
          return doDelete(selectedRowsRef.current);
        }
      });
    }
  };

  const handleDeviceNameEnable = (row, enable) => {
    modalConfirm({
      title: enable
        ? t({ defaultMessage: "启用命名规则" })
        : t({ defaultMessage: "禁用命名规则" }),
      content: enable
        ? t(
            {
              defaultMessage: "是否确认启用（{name}）该命名规则？"
            },
            { name: row.name }
          )
        : t(
            {
              defaultMessage: "是否确认禁用（{name}）该命名规则？"
            },
            { name: row.name }
          ),
      onConfirm: () => {
        return doEnable(row, enable);
      }
    });
  };

  const handleDeviceNameEdit = row => {
    history.push("/setting/device-naming/edit/" + row.id);
  };

  const handleDeviceNameView = row => {
    history.push("/setting/device-naming/info/" + row.id);
  };

  useEffect(() => {
    handleReloadDataTable(true);
    handleReloadDataTable2(true);
  }, [currentCompanyId]); // eslint-disable-line

  return (
    <div className={classes.scrollMain}>
      <Paper elevation={0} style={{minHeight: 250}}>
        <HasPermission permission="device_naming_method_view">
          <>
            <Typography variant="h5" style={{ marginBottom: 16 }}>
              {t({ defaultMessage: "公司默认命名规则" })}
            </Typography>
            <CompanyDeviceNamingDatatable
              ref={tableRef2}
              onView={handleDeviceNameView}
              onEdit={handleDeviceNameEdit}
              onDisable={handleDeviceNameEnable}
              onEnable={handleDeviceNameEnable}
              showFooter={false}
            />
          </>
        </HasPermission>
      </Paper>
      <Paper elevation={0} style={{ marginTop: 16, minHeight: 410 }}>
        <Typography variant="h5" style={{ marginBottom: 16 }}>
          {t({ defaultMessage: "门店命名规则" })}
        </Typography>
        <form onSubmit={handleSubmit}>
          <div className={classes.tableAction}>
            <SearchInput
              placeholder={t({ defaultMessage: "搜索" })}
              name="keywords"
              icon={null}
              style={{ width: 342 }}
              value={values.keywords}
              onChange={handleChange}
            />
            <HasPermission permission="device_naming_method_add">
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 8, width: 150 }}
                onClick={handleCreate}
              >
                {t({ defaultMessage: "添加命名规则" })}
              </Button>
            </HasPermission>
          </div>
          <div className={classes.tableQueryForm}>
            <FormItem
              label={t({ defaultMessage: "OS类型" })}
              className={classes.formItem}
            >
              <OSType
                name="osType"
                value={values.osType}
                onChange={handleChange}
              />
            </FormItem>
            <FormItem
              label={t({ defaultMessage: "状态" })}
              className={classes.formItem}
            >
              <DeviceNamingStatus
                name="status"
                value={values.status}
                onChange={handleChange}
              />
            </FormItem>
            <FormItem className={classes.formItem}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ marginRight: 8 }}
              >
                {t({ defaultMessage: "查询" })}
              </Button>
              <Button variant="outlined" onClick={resetQuery}>
                {t({ defaultMessage: "重置" })}
              </Button>
            </FormItem>
          </div>
        </form>
        <DeviceNamingDatatable
          ref={tableRef}
          getSearchParams={getSearchParams}
          onEnable={handleEnable}
          onAssociated={handleAssociated}
          onDelete={handleDelete}
          onViewMerchants={handleViewMerchants}
          onView={handleView}
          onEdit={handleEdit}
          onLoaded={handleLoaded}
          footer={
            <>
              <HasPermission permission="device_naming_method_enable_disable">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && updateType.current === "enable"}
                  onClick={() => handleBatchEnable(true)}
                >
                  {t({ defaultMessage: "批量启用" })}
                </SubmitButton>
              </HasPermission>
              <HasPermission permission="device_naming_method_enable_disable">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && updateType.current === "disable"}
                  onClick={() => handleBatchEnable(false)}
                >
                  {t({ defaultMessage: "批量禁用" })}
                </SubmitButton>
              </HasPermission>
              <HasPermission permission="device_naming_method_delete">
                <SubmitButton
                  variant="outlined"
                  color="primary"
                  loading={loading && updateType.current === "delete"}
                  onClick={handleBatchDelete}
                >
                  {t({ defaultMessage: "批量删除" })}
                </SubmitButton>
              </HasPermission>
            </>
          }
        />
      </Paper>
      <ViewMerchantsModal
        open={viewMerchantModalOpen}
        row={currentRow.current}
        onClose={() => setViewMerchantModalOpen(false)}
      />
      <TransferGroupModal
        open={transferGroupModalOpen}
        title={t({ defaultMessage: "选择门店" })}
        description={false}
        defaultCheckAll={false}
        userMode={false}
        checkEmpty={false}
        checkedKeys={currentRowGroupIds.current}
        onClose={() => setTransferGroupModalOpen(false)}
        onConfirm={handleTransferGroupConfirm}
      />
    </div>
  );
};

export default NamingList;
